import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { formatPts } from '../lib/format';

/**
 * Wallet -> History tab. A proper trade-history / portfolio track-record
 * surface: one row per clan the user has EVER traded (including fully
 * exited positions), expandable to show every individual buy & sell with
 * fill price and per-trade realized P/L.
 *
 * Numbers align 1:1 with /wallet Holdings:
 *   - `unrealized_pl` uses the same walk-the-curve sellback logic as
 *     /wallet/portfolio, so the number here matches the Holdings tab.
 *   - `realized_pl` prefers the stored `realized_pnl` on the trade doc;
 *     falls back to a FIFO chronological replay for legacy sells.
 *   - `total_pl` = realized + unrealized (user choice: option `b`).
 */

// Locale-aware money formatters — hop through the shared `formatPts`
// helper so numbers here show thousands separators (e.g. `301,159.11`)
// consistent with the rest of the wallet and adapt to i18n locale.
const money2 = (n) => formatPts(n, 2);
const money1 = (n) => formatPts(n, 1);

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Convert an ISO string to yyyy-mm-dd (for <input type="date"> value)
function isoToDate(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export default function WalletHistory() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);      // null = loading
  const [expandedHandle, setExpandedHandle] = useState(null);
  // Filter state
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'biggest_win' | 'biggest_loss'
  const [clanFilter, setClanFilter] = useState(''); // handle or ''
  const [startDate, setStartDate] = useState('');   // yyyy-mm-dd
  const [endDate, setEndDate] = useState('');

  // Full unfiltered payload — used to populate the clan-filter dropdown
  // even after a filter is active (so user can switch clans easily).
  const [allClans, setAllClans] = useState([]);

  const refresh = () => {
    const qs = new URLSearchParams();
    qs.set('sort', sortBy);
    if (clanFilter) qs.set('clan', clanFilter);
    if (startDate) qs.set('start', startDate);
    if (endDate) qs.set('end', endDate + 'T23:59:59');
    api.get(`/wallet/portfolio-history?${qs.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ lifetime: { realized_pl: 0, unrealized_pl: 0, total_pl: 0, clans_touched: 0, total_trades: 0 }, clans: [] }));
  };

  // Pull the full list once (no filters) so the clan dropdown always has
  // every clan the user has touched, even while a filter is active.
  useEffect(() => {
    api.get('/wallet/portfolio-history?sort=recent')
      .then(({ data }) => setAllClans((data.clans || []).map((c) => ({ handle: c.handle, display_name: c.display_name }))))
      .catch(() => setAllClans([]));
  }, []);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, clanFilter, startDate, endDate]);

  const clearFilters = () => {
    setSortBy('recent');
    setClanFilter('');
    setStartDate('');
    setEndDate('');
  };

  const hasFilters = sortBy !== 'recent' || clanFilter || startDate || endDate;

  if (data === null) {
    return <div className="loading-shell" style={{ minHeight: 200 }}>Loading history…</div>;
  }

  const { lifetime, clans } = data;
  const isEmpty = clans.length === 0 && !hasFilters;
  const isFilteredEmpty = clans.length === 0 && hasFilters;

  return (
    <div className="wallet-panel wh-panel" data-testid="wallet-panel-history">
      {/* KPI strip — lifetime realized + unrealized + total */}
      <div className="wh-kpis" data-testid="wh-kpis">
        <KpiCard
          label="Realized P/L"
          value={lifetime.realized_pl}
          hint="Locked-in from sells"
          testId="wh-kpi-realized"
        />
        <KpiCard
          label="Unrealized P/L"
          value={lifetime.unrealized_pl}
          hint="On currently held Bits"
          testId="wh-kpi-unrealized"
        />
        <KpiCard
          label="Total P/L"
          value={lifetime.total_pl}
          hint={`${lifetime.total_trades} trades · ${lifetime.clans_touched} clan${lifetime.clans_touched === 1 ? '' : 's'}`}
          bold
          testId="wh-kpi-total"
        />
      </div>

      {/* Filter bar */}
      <div className="wh-filters" data-testid="wh-filters">
        <label className="wh-filter">
          <span className="wh-filter-lbl">Sort</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="wh-filter-input"
            data-testid="wh-sort"
          >
            <option value="recent">Most recent</option>
            <option value="biggest_win">Biggest winners</option>
            <option value="biggest_loss">Biggest losers</option>
          </select>
        </label>

        <label className="wh-filter">
          <span className="wh-filter-lbl">Clan</span>
          <select
            value={clanFilter}
            onChange={(e) => setClanFilter(e.target.value)}
            className="wh-filter-input"
            data-testid="wh-clan"
          >
            <option value="">All clans</option>
            {allClans.map((c) => (
              <option key={c.handle} value={c.handle}>{c.display_name || `@${c.handle}`}</option>
            ))}
          </select>
        </label>

        <label className="wh-filter">
          <span className="wh-filter-lbl">From</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="wh-filter-input"
            data-testid="wh-start"
          />
        </label>

        <label className="wh-filter">
          <span className="wh-filter-lbl">To</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="wh-filter-input"
            data-testid="wh-end"
          />
        </label>

        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="wh-clear-btn"
            data-testid="wh-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Empty states */}
      {isEmpty && (
        <div className="wallet-empty" data-testid="wh-empty">
          <div className="we-icon">📊</div>
          <h3>No trade history yet</h3>
          <p>Buy your first Bit to start building your track record. Every trade — win or loss — gets logged here so you can look back and learn.</p>
          <Link to="/home" className="btn-primary" style={{ marginTop: 14 }}>
            Discover creators
          </Link>
        </div>
      )}

      {isFilteredEmpty && (
        <div className="wallet-empty" data-testid="wh-empty-filtered">
          <div className="we-icon">🔎</div>
          <h3>No trades match these filters</h3>
          <p>Try widening the date range or clearing the clan filter.</p>
          <button type="button" onClick={clearFilters} className="btn-primary" style={{ marginTop: 14 }}>
            Clear filters
          </button>
        </div>
      )}

      {/* Clan roll-up rows */}
      {clans.length > 0 && (
        <div className="wh-clans">
          {clans.map((c) => (
            <ClanRow
              key={c.handle}
              clan={c}
              expanded={expandedHandle === c.handle}
              onToggle={() => setExpandedHandle(expandedHandle === c.handle ? null : c.handle)}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, hint, bold, testId }) {
  const positive = value >= 0;
  return (
    <div className={`wh-kpi ${bold ? 'wh-kpi-total' : ''}`} data-testid={testId}>
      <div className="wh-kpi-lbl">{label}</div>
      <div className={`wh-kpi-val ${positive ? 'pos' : 'neg'}`}>
        {positive ? '+' : '−'}{money2(Math.abs(value))} <span className="wh-kpi-unit">pts</span>
      </div>
      {hint && <div className="wh-kpi-hint">{hint}</div>}
    </div>
  );
}

function ClanRow({ clan, expanded, onToggle, t }) {
  const totalPositive = clan.total_pl >= 0;
  const gradCss = `linear-gradient(135deg, ${clan.gradient?.[0] || '#8B5CF6'}, ${clan.gradient?.[1] || '#F2679E'})`;

  return (
    <div className={`wh-clan ${expanded ? 'expanded' : ''}`} data-testid={`wh-clan-${clan.handle}`}>
      <button
        type="button"
        className="wh-clan-head"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`wh-clan-toggle-${clan.handle}`}
      >
        <div className="wh-clan-id">
          {clan.avatar ? (
            <img className="wh-clan-avatar" src={clan.avatar} alt="" />
          ) : (
            <div className="wh-clan-avatar" style={{ background: gradCss }}>
              <span className="wh-clan-initials">{clan.initials}</span>
            </div>
          )}
          <div className="wh-clan-name">
            <div className="wh-clan-title">
              {clan.display_name}
              {clan.is_closed && <span className="wh-closed-badge" title="You've fully sold out of this clan">Closed</span>}
            </div>
            <div className="wh-clan-sub">@{clan.handle} · {clan.trades.length} trade{clan.trades.length === 1 ? '' : 's'}</div>
          </div>
        </div>

        <div className="wh-clan-metrics">
          <div className="wh-metric">
            <span className="wh-metric-lbl">Invested</span>
            <span className="wh-metric-val">{money2(clan.bought_pts)} pts</span>
            <span className="wh-metric-sub">{clan.bought_qty} Bit{clan.bought_qty === 1 ? '' : 's'}</span>
          </div>
          <div className="wh-metric">
            <span className="wh-metric-lbl">Cashed out</span>
            <span className="wh-metric-val">{money2(clan.sold_pts)} pts</span>
            <span className="wh-metric-sub">{clan.sold_qty} Bit{clan.sold_qty === 1 ? '' : 's'}</span>
          </div>
          <div className="wh-metric">
            <span className="wh-metric-lbl">Holding</span>
            <span className="wh-metric-val">{money2(clan.current_value)} pts</span>
            <span className="wh-metric-sub">{clan.current_qty} Bit{clan.current_qty === 1 ? '' : 's'}</span>
          </div>
          <div className={`wh-metric wh-metric-total ${totalPositive ? 'pos' : 'neg'}`}>
            <span className="wh-metric-lbl">Total P/L</span>
            <span className="wh-metric-val">
              {totalPositive ? '+' : '−'}{money2(Math.abs(clan.total_pl))} pts
            </span>
            <span className="wh-metric-sub">
              {totalPositive ? '+' : '−'}{money1(Math.abs(clan.total_pl_pct))}%
            </span>
          </div>
        </div>

        <span className={`wh-caret ${expanded ? 'up' : ''}`} aria-hidden="true">▾</span>
      </button>

      {expanded && (
        <div className="wh-trades" data-testid={`wh-trades-${clan.handle}`}>
          <div className="wh-pl-split">
            <span className="wh-pl-chip realized" data-testid={`wh-realized-${clan.handle}`}>
              Realized {clan.realized_pl >= 0 ? '+' : '−'}{money2(Math.abs(clan.realized_pl))} pts
            </span>
            <span className="wh-pl-chip unrealized" data-testid={`wh-unrealized-${clan.handle}`}>
              Unrealized {clan.unrealized_pl >= 0 ? '+' : '−'}{money2(Math.abs(clan.unrealized_pl))} pts
            </span>
            <span className="wh-pl-chip range">
              {fmtDate(clan.first_trade_at)} → {fmtDate(clan.last_trade_at)}
            </span>
          </div>

          {/* Projected sellback tiers — only visible for OPEN positions.
              Backend already emits `projections=[]` for closed rows, and
              we double-guard on !is_closed here so a fully-cashed-out
              Clan never shows a projection block. */}
          {!clan.is_closed && clan.projections && clan.projections.length > 0 && (
            <div className="wh-proj" data-testid={`wh-proj-${clan.handle}`}>
              <div className="wh-proj-title">
                If <span className="wh-proj-clan">@{clan.handle}</span> grows to…
              </div>
              <div className="wh-proj-grid">
                {clan.projections.map((p) => (
                  <div className="wh-proj-tier" key={p.key} data-testid={`wh-proj-${clan.handle}-${p.key}`}>
                    <div className="wh-proj-lbl">{p.label}</div>
                    <div className="wh-proj-val">{money2(p.value)} <span className="wh-proj-unit">pts</span></div>
                    <div className={`wh-proj-delta ${p.delta_pct > 0 ? 'pos' : (p.delta_pct < 0 ? 'neg' : '')}`}>
                      {p.delta_pct > 0 ? '+' : ''}{money1(p.delta_pct)}%
                    </div>
                  </div>
                ))}
              </div>
              <div className="wh-proj-hint">
                Projected sellback if the clan reaches this supply. Not a promise — Bits can lose value if fans sell out.
              </div>
            </div>
          )}

          {/* Action buttons — Buy more / Sell / View clan. Hidden on
              fully-closed positions since there's nothing left to buy
              more OF or sell (users would still see View clan though,
              in case they want to jump back into the clan surface). */}
          <div className="wh-actions" data-testid={`wh-actions-${clan.handle}`}>
            {!clan.is_closed && (
              <Link
                to={`/trade/${clan.handle}?mode=buy`}
                className="wh-action-btn wh-action-buy"
                data-testid={`wh-buy-${clan.handle}`}
              >
                {t('wallet.buyMore')}
              </Link>
            )}
            {!clan.is_closed && (
              <Link
                to={`/trade/${clan.handle}?mode=sell`}
                className="wh-action-btn wh-action-sell"
                data-testid={`wh-sell-${clan.handle}`}
              >
                {t('wallet.sell')}
              </Link>
            )}
            <Link
              to={`/creator/${clan.handle}`}
              className="wh-action-btn wh-action-view"
              data-testid={`wh-view-${clan.handle}`}
            >
              {t('wallet.viewClan')}
            </Link>
          </div>

          <div className="wh-trades-list">
            {clan.trades.map((t) => (
              <TradeRow key={t.trade_id} trade={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TradeRow({ trade }) {
  const isBuy = trade.side === 'buy';
  const realized = trade.realized_pnl;
  const realizedPositive = realized != null && realized >= 0;
  return (
    <div className={`wh-trade ${trade.side}`} data-testid={`wh-trade-${trade.trade_id}`}>
      <div className="wh-trade-cell wh-trade-side">
        <span className={`wh-trade-badge ${trade.side}`}>{isBuy ? 'BUY' : 'SELL'}</span>
        <span className="wh-trade-date">{fmtDateTime(trade.created_at)}</span>
      </div>
      <div className="wh-trade-cell">
        <span className="wh-trade-lbl">Qty</span>
        <span className="wh-trade-val">{trade.qty} Bit{trade.qty === 1 ? '' : 's'}</span>
      </div>
      <div className="wh-trade-cell">
        <span className="wh-trade-lbl">Fill price</span>
        <span className="wh-trade-val">{money2(trade.price_per_bit)} pts</span>
      </div>
      <div className="wh-trade-cell">
        <span className="wh-trade-lbl">{isBuy ? 'Paid' : 'Received'}</span>
        <span className="wh-trade-val">{money2(trade.total)} pts</span>
      </div>
      <div className="wh-trade-cell wh-trade-pl">
        {isBuy ? null : realized != null ? (
          <>
            <span className="wh-trade-lbl">Realized</span>
            <span className={`wh-trade-val ${realizedPositive ? 'pos' : 'neg'}`}>
              {realizedPositive ? '+' : '−'}{money2(Math.abs(realized))} pts
            </span>
          </>
        ) : (
          <span className="wh-trade-hint">—</span>
        )}
      </div>
    </div>
  );
}
